import { useRef } from 'react'
import { cn } from '@/lib/utils'

const LONG_PRESS_MS = 450
const MOVE_CANCEL_PX = 10

export interface KanbanCardData {
  id: string
  time: string
  category: string
  pickupLocation: string
  dropoffLocation: string
  amount: number
}

export function KanbanColumn({
  title,
  subtitle,
  isEmpty,
  isDragOver,
  isCarrying,
  onDragOver,
  onDragLeave,
  onDrop,
  children,
}: {
  title: string
  subtitle: string
  isEmpty: boolean
  isDragOver: boolean
  /** 目前畫面上是否有任何一張卡片被拿起（滑鼠拖曳中 或 觸控長按中）——
   * 為 true 時，每一欄都會變成合法的「點擊放下」目標，並顯示提示樣式。 */
  isCarrying: boolean
  onDragOver: () => void
  onDragLeave: () => void
  onDrop: () => void
  children: React.ReactNode
}) {
  return (
    <div
      onDragOver={(e) => {
        e.preventDefault()
        onDragOver()
      }}
      onDragLeave={onDragLeave}
      onDrop={(e) => {
        e.preventDefault()
        onDrop()
      }}
      onTouchEnd={() => {
        // 只有點在「欄位背景」或「非被拿起的卡片」時才會冒泡到這裡——
        // 被拿起卡片自己的 tap（取消）跟剛長按完的 tap（拿起）
        // 都會在 OrderCard 內 stopPropagation，不會冒泡上來。見下方 OrderCard。
        if (isCarrying) onDrop()
      }}
      className={cn(
        'flex w-64 shrink-0 flex-col rounded-xl border bg-white dark:bg-gray-800 shadow-sm transition-colors',
        isDragOver
          ? 'border-indigo-400 bg-indigo-50/40 dark:bg-indigo-900/20'
          : isCarrying
            ? 'border-indigo-300 border-dashed bg-indigo-50/20 dark:bg-indigo-900/10'
            : 'border-gray-200 dark:border-gray-700',
      )}
    >
      <div className="border-b border-gray-200 dark:border-gray-700 px-3 py-2.5">
        <p className="text-sm font-semibold text-gray-900 dark:text-gray-100">{title}</p>
        <p className="text-xs text-gray-400">{subtitle}</p>
      </div>
      <div className="min-h-[6rem] flex-1 space-y-2 p-2">
        {children}
        {isEmpty && (
          <p className="px-1 py-4 text-center text-xs text-gray-300 dark:text-gray-600">
            {isCarrying ? '點此指派' : '拖放至此'}
          </p>
        )}
      </div>
    </div>
  )
}

export function OrderCard({
  card,
  categoryBadgeClass,
  isDragging,
  anyCarried,
  onDragStart,
  onDragEnd,
  onClick,
}: {
  card: KanbanCardData
  categoryBadgeClass: string
  isDragging: boolean
  /** 是否有任何卡片（含自己）正被拿起——滑鼠拖曳中 或 觸控長按中。 */
  anyCarried: boolean
  onDragStart: () => void
  onDragEnd: () => void
  /** 沒有任何卡片被拿起時，單純點卡片 = 開啟訂單詳情（可選）。 */
  onClick?: () => void
}) {
  const longPressTimer = useRef<ReturnType<typeof setTimeout> | null>(null)
  const longPressFired = useRef(false)
  const touchMoved = useRef(false)
  const startPos = useRef<{ x: number; y: number } | null>(null)

  function clearLongPressTimer() {
    if (longPressTimer.current) {
      clearTimeout(longPressTimer.current)
      longPressTimer.current = null
    }
  }

  function handleTouchStart(e: React.TouchEvent) {
    touchMoved.current = false
    const touch = e.touches[0]
    startPos.current = { x: touch.clientX, y: touch.clientY }

    // 已經被拿起、或有別的卡片被拿起 → 這次觸碰交給 touchend 處理
    // （取消拿起 或 冒泡當作放下），不要重新啟動長按計時。
    if (isDragging || anyCarried) return

    longPressTimer.current = setTimeout(() => {
      longPressFired.current = true
      onDragStart()
    }, LONG_PRESS_MS)
  }

  function handleTouchMove(e: React.TouchEvent) {
    if (!startPos.current) return
    const touch = e.touches[0]
    const dx = Math.abs(touch.clientX - startPos.current.x)
    const dy = Math.abs(touch.clientY - startPos.current.y)
    if (dx > MOVE_CANCEL_PX || dy > MOVE_CANCEL_PX) {
      touchMoved.current = true
      clearLongPressTimer() // 判定成滑動頁面，不是長按
    }
  }

  function handleTouchEnd(e: React.TouchEvent) {
    clearLongPressTimer()

    if (longPressFired.current) {
      // 剛長按完放開手指——這是「拿起」動作本身的放開，
      // 不要讓它又冒泡上去被當成「放到自己這欄」。
      longPressFired.current = false
      e.stopPropagation()
      return
    }
    if (touchMoved.current) return // 是滑動，不是點擊

    if (isDragging) {
      // 對「已經被拿起的卡片」再點一次 = 取消拿起。
      e.stopPropagation()
      onDragEnd()
      return
    }
    if (!anyCarried) {
      onClick?.()
      return
    }
    // 其餘情況：別張卡片被拿起，讓 tap 冒泡到所在的欄位，交給欄位當作放下目標。
  }

  return (
    <div
      draggable
      onDragStart={(e) => {
        e.dataTransfer.effectAllowed = 'move'
        onDragStart()
      }}
      onDragEnd={onDragEnd}
      onTouchStart={handleTouchStart}
      onTouchMove={handleTouchMove}
      onTouchEnd={handleTouchEnd}
      onClick={() => {
        if (!anyCarried) onClick?.()
      }}
      className={cn(
        'touch-manipulation cursor-grab select-none space-y-1 rounded-lg border p-2.5 text-xs shadow-sm transition-all active:cursor-grabbing bg-white dark:bg-gray-900',
        isDragging
          ? 'border-indigo-400 opacity-70 ring-2 ring-indigo-400'
          : 'border-gray-200 dark:border-gray-700 opacity-100',
      )}
    >
      <div className="flex items-center justify-between gap-1">
        <span className="font-semibold text-gray-900 dark:text-gray-100">{card.time}</span>
        <span className={cn('inline-block px-1.5 py-0.5 rounded-full text-[10px] font-semibold', categoryBadgeClass)}>
          {card.category}
        </span>
      </div>
      <div className="text-gray-600 dark:text-gray-400">
        {card.pickupLocation} → {card.dropoffLocation}
      </div>
      <div className="font-medium text-gray-900 dark:text-gray-100">${Math.round(card.amount)}</div>
    </div>
  )
}
